import { WARNING_ORANGE } from "@finway-group/shared/lib/consts"
import { Expense, RightEnum, Vendor, VendorTypeEnum } from "@finway-group/shared/lib/models"
import { Button, Select, Tooltip } from "antd"
import React from "react"
import { AlertTriangle, RotateCcw } from "react-feather"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { Link } from "react-router-dom"

import { patchExpense, syncExpense } from "Shared/store/actions/expense/expenseActions"

import OcrIcon from "../../../../../components/icons/ocr"
import { AuthzService, ExpenseHttpService, NotificationService } from "../../../../../shared/services"
import {
    checkIsVendorPaymentDataMismatch,
    getBicOptions,
    getIbanOptions,
    isExpenseCreditorOutdated,
    isInvoiceBicDifferent,
    isInvoiceIbanDifferent,
} from "../../../../../shared/utils/expense.utils"
import { getIBANPrintFormat } from "../../../../../shared/utils/helper.utils"
import { UserCreditor, getVendorLocationLabel } from "../../../../../shared/utils/vendor.utils"

interface VendorTabProps {
    expense: Expense
    isExpenseUpdating: boolean
    expenseCreditor: Vendor | UserCreditor
    setIsExpenseUpdating?: React.Dispatch<React.SetStateAction<boolean>>
    setSelectedExpense?: (expense: Expense | undefined) => void
}

export const VendorTabContent = ({ expense, expenseCreditor, isExpenseUpdating, setIsExpenseUpdating, setSelectedExpense }: VendorTabProps) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const onExpenseVendorDataUpdate = async (patchVendorData: Partial<Pick<Vendor, "iban" | "bic">>) => {
        try {
            setIsExpenseUpdating?.(true)
            const patchPayload = {
                vendor: {
                    _id: expense.vendor?._id,
                    ...patchVendorData,
                },
                vendorType: VendorTypeEnum.VENDOR,
            }

            const updatedExpenseResponse = await patchExpense(expense.id, patchPayload)(dispatch)
            setSelectedExpense?.(updatedExpenseResponse)
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
        } finally {
            setIsExpenseUpdating?.(false)
        }
    }

    const onSyncExpenseWithCreditor = async () => {
        try {
            await ExpenseHttpService.syncExpenseCreditor(expense.id)
            dispatch(syncExpense(expense.id))
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
        }
    }

    const renderCreditorData = () => {
        if (!expenseCreditor) {
            return <></>
        }

        if (expenseCreditor instanceof Vendor) {
            const expenseVendor = expense.vendor

            if (!expenseVendor) {
                return null
            }
            const vendor = expenseCreditor

            const vendorLocationLabel = getVendorLocationLabel(expenseVendor)

            const ibanOptions = getIbanOptions(expense)
            const bicOptions = getBicOptions(expense)

            const isVendorPaymentDataMismatch = checkIsVendorPaymentDataMismatch(expense)
            return (
                <ul className="description-list pb-10 overflow-hidden">
                    <li>
                        <span className="title">{t("label:name")}</span>
                        <span className="text truncate max-w-250">
                            <Link to={`/vendors/${expenseVendor?._id}`}>{expenseVendor?.name}</Link>
                        </span>
                    </li>
                    <li>
                        <span className="title">{t("label:creditor_number")}</span>
                        <span className="text truncate max-w-250">{expenseVendor?.creditorNumber ?? "-"}</span>
                    </li>
                    <li>
                        <span className="title">{t("label:description")}</span>
                        <span className="text truncate max-w-250">{vendor.description}</span>
                    </li>
                    <li>
                        <span className="title">{t("label:location")}</span>
                        <span className="text truncate max-w-250">{vendorLocationLabel}</span>
                    </li>
                    <li>
                        <span className="title">{t("label:vat_number")}</span>
                        <span className="text">{expenseVendor?.vatNumber ?? "-"}</span>
                    </li>
                    <li>
                        <span className="title ">{t("label:iban")}</span>
                        <div className="text">
                            {isVendorPaymentDataMismatch && ibanOptions.length > 0 ? (
                                <div className="flex flex-row items-center ">
                                    {isInvoiceIbanDifferent(expense) && (
                                        <div>
                                            <Tooltip title={t("info:ocr.iban_mismatch")}>
                                                <AlertTriangle fill={WARNING_ORANGE} size={24} stroke="#FFFFFF" className="mr-4" />
                                            </Tooltip>
                                        </div>
                                    )}
                                    <Select
                                        defaultValue={expenseVendor?.iban ?? t("missing:select_option_no_value")}
                                        value={expenseVendor?.iban ?? ""}
                                        dropdownMatchSelectWidth={false}
                                        onChange={(value) => onExpenseVendorDataUpdate({ iban: value })}
                                        className="overflow-auto min-w-120"
                                        disabled={isExpenseUpdating}
                                    >
                                        {ibanOptions.map((option) => (
                                            <Select.Option key={option.value} value={option.value ?? ""} label={option.label} className="overflow-auto">
                                                <div className="flex flex-row items-center">
                                                    <span className="overflow-auto"> {option.label}</span>
                                                    {option.isInvoiceIban && <OcrIcon className="pl-6" />}
                                                </div>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                            ) : expenseVendor?.iban ? (
                                getIBANPrintFormat(expenseVendor.iban)
                            ) : (
                                t("missing:select_option_no_value")
                            )}
                        </div>
                    </li>
                    <li>
                        <span className="title">{t("label:bic")}</span>
                        <div className="text">
                            {isVendorPaymentDataMismatch && bicOptions.length > 0 ? (
                                <div className="flex flex-row items-center ">
                                    {isInvoiceBicDifferent(expense) && (
                                        <div>
                                            <Tooltip title={t("info:ocr.bic_mismatch")}>
                                                <AlertTriangle fill={WARNING_ORANGE} size={24} stroke="#FFFFFF" className="mr-4" />
                                            </Tooltip>
                                        </div>
                                    )}
                                    <Select
                                        defaultValue={expenseVendor?.bic ?? t("missing:select_option_no_value")}
                                        value={expenseVendor?.bic ?? ""}
                                        dropdownMatchSelectWidth={false}
                                        onChange={(value) => onExpenseVendorDataUpdate({ bic: value })}
                                        className="overflow-auto min-w-120"
                                        disabled={isExpenseUpdating}
                                    >
                                        {bicOptions.map((option) => (
                                            <Select.Option key={option.value} value={option.value ?? ""} label={option.label} className="overflow-auto">
                                                <div className="flex flex-row items-center">
                                                    <span className="overflow-auto"> {option.label}</span>
                                                    {option.isInvoiceBic && <OcrIcon className="pl-6" />}
                                                </div>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                            ) : (
                                expenseVendor?.bic || t("missing:select_option_no_value")
                            )}
                        </div>
                    </li>
                    <li>
                        <span className="title">{t("label:contact_person")}</span>
                        <span className="text truncate max-w-250">{vendor.contactPersonName || "-"}</span>
                    </li>
                </ul>
            )
        }

        const expenseEmployee = expense.creditorUser

        return (
            <ul className="description-list pb-10 overflow-auto">
                <li>
                    <span className="title">{t("label:name")}</span>
                    <span className="text truncate max-w-250">
                        <Link to={`/employees/${expenseEmployee?._id}`}>
                            {expenseEmployee?.firstName} {expenseEmployee?.lastName}
                        </Link>
                    </span>
                </li>
                <li>
                    <span className="title">{t("label:email")}</span>
                    <span className="text truncate max-w-250">{expenseEmployee?.email}</span>
                </li>
                <li>
                    <span className="title">{t("label:location")}</span>
                    <span className="text truncate max-w-250">{expenseEmployee?.location}</span>
                </li>
                <li>
                    <span className="title">{t("label:iban")}</span>
                    <span className="text">{expenseEmployee?.iban || "-"}</span>
                </li>
                <li>
                    <span className="title">{t("label:bic")}</span>
                    <span className="text">{expenseEmployee?.bic || "-"}</span>
                </li>
                {AuthzService.isRightGrantedForLoggedInUser(RightEnum.EMPLOYEE__ALL__READ) && (
                    <li>
                        <span className="title">{t("label:creditor_number")}</span>
                        <span className="text truncate max-w-250">{expenseEmployee?.creditorNumber || "-"}</span>
                    </li>
                )}
            </ul>
        )
    }

    return (
        <>
            {isExpenseCreditorOutdated({
                vendorType: expense.vendorType,
                vendor: expenseCreditor,
                vendorReference: expense.getCreditor()?.source,
            }) &&
                !expense.gobdInvoiceComplianceDate && (
                    <Tooltip title={t("label:vendor_data_changed")}>
                        <Button className="w-full mt-5 mb-10 flex-1 btn-default p-5" onClick={onSyncExpenseWithCreditor}>
                            <RotateCcw />
                            <span>{t("action:request.sync")}</span>
                        </Button>
                    </Tooltip>
                )}
            {renderCreditorData()}
        </>
    )
}
